import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import type { HealthResponse } from '@teams-avatar/shared';
import { api } from '../lib/api';
import { formatDuration } from '../lib/format';

const POLL_INTERVAL_MS = 15_000;

const DOCKER_LABELS: Record<HealthResponse['checks']['docker'], string> = {
  ok: 'erreichbar',
  unavailable: 'nicht erreichbar',
  not_checked: 'nicht geprueft',
};

const ACS_LABELS: Record<HealthResponse['checks']['acs'], string> = {
  ok: 'ok',
  error: 'Fehler',
  not_configured: 'nicht konfiguriert',
};

/**
 * Zeigt vor allem, ob der Docker-Socket erreichbar ist. Ohne den kann die
 * Control Plane keine Worker starten, und das soll man sehen, bevor der erste
 * Join-Versuch scheitert.
 */
export function HealthPanel(): JSX.Element {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const poll = async (): Promise<void> => {
      try {
        const result = await api.health();
        if (cancelled) return;
        setHealth(result);
        setError(null);
      } catch (cause) {
        if (cancelled) return;
        setError(cause instanceof Error ? cause.message : String(cause));
      }
    };

    void poll();
    const timer = window.setInterval(() => void poll(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  if (error !== null) {
    return (
      <div className="rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger">
        Control Plane nicht erreichbar: {error}
      </div>
    );
  }

  if (health === null) {
    return (
      <div className="rounded-xl border border-border bg-bg px-4 py-3 text-sm text-fg-muted">
        Lade Systemstatus …
      </div>
    );
  }

  const dockerOk = health.checks.docker === 'ok';
  const dbOk = health.checks.database === 'ok';
  // "nicht konfiguriert" ist kein Fehler, aber auch nicht in Ordnung — solange
  // das so steht, kann keine Session einem Meeting beitreten.
  const acsTone =
    health.checks.acs === 'ok' ? 'ok' : health.checks.acs === 'error' ? 'bad' : 'neutral';

  return (
    <div className="rounded-xl border border-border bg-bg px-4 py-3.5 sm:px-5">
      <dl className="flex flex-wrap gap-x-8 gap-y-3 text-sm">
        <Item label="Version" value={`${health.version} (Phase ${health.phase})`} />
        <Item label="Uptime" value={formatDuration(health.uptimeSeconds)} />
        <Item label="SQLite" value={dbOk ? 'ok' : 'Fehler'} tone={dbOk ? 'ok' : 'bad'} />
        <Item
          label="Docker-Socket"
          value={DOCKER_LABELS[health.checks.docker]}
          tone={dockerOk ? 'ok' : 'bad'}
        />
        <Item
          label="ACS"
          value={ACS_LABELS[health.checks.acs]}
          tone={acsTone}
          hint={alterDerPruefung(health.acsCheckedAt)}
        />
      </dl>

      {/*
        Ein ACS-Fehler bedeutet: KEINE Session kann einem Meeting beitreten. Das
        als eine Zeile unter vielen zu zeigen hat am 28.10.2026 Zeit gekostet -
        die Oberflaeche meldete "ok" aus einer 26 Tage alten Pruefung, und der
        Fehler sah aus wie ein Problem des gerade gewaehlten Avatars.
      */}
      {health.checks.acs === 'error' && (
        <p className="mt-3 rounded-lg bg-danger/10 px-3 py-2 text-sm text-danger">
          ACS lehnt gerade ab — <strong>keine</strong> Session kann einem Meeting beitreten,
          unabhaengig vom gewaehlten Avatar. Meist ist der Zugriffsschluessel in Azure neu
          erzeugt worden; dann muss ACS_CONNECTION_STRING erneuert werden.
        </p>
      )}
      {!dockerOk && health.dockerError !== undefined && (
        <p className="mt-2.5 text-xs text-danger">{health.dockerError}</p>
      )}
      {health.acsError !== undefined && (
        <p className="mt-2.5 text-xs text-danger">ACS: {health.acsError}</p>
      )}
    </div>
  );
}

/** "vor 3 Minuten geprueft" — damit sich "ok" von "war mal ok" unterscheiden laesst. */
function alterDerPruefung(checkedAt: string | undefined): string | undefined {
  if (checkedAt === undefined) return undefined;
  const alterMs = Date.now() - Date.parse(checkedAt);
  if (!Number.isFinite(alterMs) || alterMs < 0) return undefined;
  const minuten = Math.round(alterMs / 60_000);
  if (minuten < 1) return 'gerade geprueft';
  if (minuten < 60) return `vor ${minuten} min geprueft`;
  const stunden = Math.round(minuten / 60);
  if (stunden < 48) return `vor ${stunden} h geprueft`;
  return `vor ${Math.round(stunden / 24)} Tagen geprueft`;
}

function Item({
  label,
  value,
  tone = 'neutral',
  hint,
}: {
  label: string;
  value: string;
  tone?: 'neutral' | 'ok' | 'bad';
  hint?: string | undefined;
}): JSX.Element {
  const toneClass =
    tone === 'ok' ? 'text-fg' : tone === 'bad' ? 'text-danger' : 'text-fg-subtle';
  return (
    <div>
      <dt className="text-[11px] tracking-wide text-fg-muted uppercase">{label}</dt>
      <dd className={`mt-0.5 font-medium ${toneClass}`}>{value}</dd>
      {hint !== undefined && <dd className="text-[11px] text-fg-muted">{hint}</dd>}
    </div>
  );
}
